import React from 'react'
import { Form, Col, Image } from 'react-bootstrap'
import { Subscription } from "rxjs"
import { Typeahead } from 'react-bootstrap-typeahead'
import { toast } from "react-toastify"
import SelectServiceTogglePanel from './SelectServiceTogglePanel'
import { ServiceDataModel, EmptyServiceData } from '../OrderModels'
import dotImage from '../../../assets/images/dot-img.png'
import SelectService, { SelectServiceProps, SelectServiceState } from './SelectService'
import { AlcoholTestSampleTypesObservable } from "../../../api/OrderRequests/OrderRequestsConstraintGet"
import { AlcoholTestSampleType } from "../../../api/OrderRequests/OrderRequestsModel/AlcoholTestSampleType"
import { ServiceTypeCode } from "../../../api/Administration/Model/ServiceType"
import { ServiceCode, IsServiceActive, ServicesByServiceSampleType, ServiceByCode } from "../../../api/Administration/Model/Service"
import { EventType } from '../../../config/Constants'
import RegulatoryModeControl from '../RegulatoryModeControl'

interface SelectAlcoholTestState extends SelectServiceState {
  // TODO: 2.0 API availableAlcoholTestSampleTypes: OrderFieldListItem[]
  availableAlcoholTestSampleTypes: AlcoholTestSampleType[]
}

export default class SelectAlcoholTest extends SelectService<SelectServiceProps, SelectAlcoholTestState> {
  defaultService: ServiceDataModel = { ...EmptyServiceData, serviceTypeCode: ServiceTypeCode.Alcohol, serviceCode: ServiceCode.None }

  subscriptions = new Subscription()

  constructor(props: SelectServiceProps) {
    super(props)

    this.defaultService = { ...EmptyServiceData, serviceTypeCode: ServiceTypeCode.Alcohol, serviceCode: ServiceCode.None,  isDOT: props.isDot }
    this.state = { ...this.defaultService, availableAlcoholTestSampleTypes: [] }
  }

  componentDidMount() {
    const selectedTest = this.props.selectedTestCode
    const isDot = this.props.isDot
    this.subscriptions.add(
      AlcoholTestSampleTypesObservable().subscribe(
        (results: AlcoholTestSampleType[]) => {
          this.setState({
            availableAlcoholTestSampleTypes: results,
            testToPerform: selectedTest && results.find(type => type.Code === selectedTest)?.Description || "",
            testToPerformObj: selectedTest ? results.find(type => type.Code === selectedTest) : undefined
          })
          if (selectedTest) {
            let serviceCode = ServiceCode.AlcoholBreath
            if (selectedTest === EventType.Breath && isDot) serviceCode = ServiceCode.AlcoholBreathDOT
            if (selectedTest === EventType.Saliva && isDot) serviceCode = ServiceCode.AlcoholSalivaDOT
            if (selectedTest === EventType.Saliva && !isDot) serviceCode = ServiceCode.AlcoholSaliva
            this.onChangeTestType(serviceCode)
          }
        },
        error => toast.error(error)
      )
    )
  }

  componentWillUnmount() {
    this.subscriptions.unsubscribe()
  }

  setInitialState = () => this.setState({ ...this.defaultService })
  
  onChangeTestType = (value: string) => {
    const orderableServices = this.props.orderableServices;
    const serviceCode = value as ServiceCode;
    const service = ServiceByCode(orderableServices, serviceCode);
    this.setState(
      {
        serviceCode: serviceCode,
        isDOT: service.DOT,
        isMarketplace: service.Marketplace
      })
  }

  onChangeTestToPerform = (selected: AlcoholTestSampleType[]) => {
    const item = selected.length > 0 ? selected[0] : undefined
    this.setState({ testToPerform: item?.Description || '', testToPerformObj: item })
  }

  onServiceAdd = () => {
    this.props.onAdd(this.state as ServiceDataModel)
    this.setInitialState()
  }

  onPanelClose = () => {
    // Clear all entered values
    this.setInitialState()
  }

  isValid = (data: ServiceDataModel): boolean => {
    if(this.isAllServicesDisabled || this.props.denyAddService){
      return false;
    }

    return typeof data.isDOT === "boolean" // type is set
      && !!data.testToPerform && data.testToPerform.length > 0 // testToPerform is set
      && (data.isDOT !== true || this.state.isValidMode)
  }

  render() {
    const { disabled, canAddDOT, canAddNonDOT, open } = this.props
    const { testToPerformObj, isDOT } = this.state
    const orderableServiceType = this.props.orderableServiceType;

    const marketplaceServicesCount = this.props.marketplaceServicesCount;
    const notMarketplaceServicesCount = this.props.notMarketplaceServicesCount;

    const canAddMarketplace = notMarketplaceServicesCount === 0;
    const canAddNotMarketplace = marketplaceServicesCount === 0;


    return (
      <SelectServiceTogglePanel title={orderableServiceType.ServiceTypeName || "ALCOHOL TEST"}
        data={this.state as ServiceDataModel}
        collapsibleId='AlcoholTestCollapsePanel'
        disabled={disabled}
        open={open}
        onSubmit={this.onServiceAdd}
        onValidate={this.isValid}
        onClose={this.onPanelClose}>
        <Form.Row>
          <Col>
          <Form.Group>
            <Form.Label className="font-weight-bold required">ALCOHOL TEST TO BE PERFORMED</Form.Label>
            <Typeahead id="alcoholTestToPerformTypeahead"
              labelKey={option => `${option.Description}`}
              filterBy={(option, props) => {
                return props.selected.length > 0 || (option?.Description || "").toLowerCase().indexOf(props.text.toLowerCase()) !== -1;
              }}
              onChange={this.onChangeTestToPerform}
              options={this.state.availableAlcoholTestSampleTypes || []}
              placeholder="Enter the Sample Type name"
              isInvalid={!testToPerformObj}
              disabled={!!this.props.selectedTestCode}
              selected={testToPerformObj ? [testToPerformObj as AlcoholTestSampleType] : []} />
            </Form.Group>
          </Col>
        </Form.Row>
        <Form.Row>
          <Col xs="11" md="11" sm="11">
            <Form.Group className={!this.state.serviceCode ? "invalid-section-border p-2" : "section-border p-2"}>
              {this.getServicesComponents()}
            </Form.Group>
          </Col>
          <Col xs="1" md="1" sm="1" hidden={isDOT !== true}>
            <Image src={dotImage} />
          </Col>
        </Form.Row>
        {isDOT && <RegulatoryModeControl
          defaults={this.props.defaults}
          regulatoryModeID={this.props.regulatoryModeID}
          employeeCategoryID={this.props.employeeCategoryID}
          onChange={(isValid, mode, cat) => this.setState({ isValidMode: isValid, regulatoryModeObj: mode, categoryObj: cat })}
        />}
      </SelectServiceTogglePanel>
    )
  }

  getServicesComponents() {
    const { disabled, canAddDOT, canAddNonDOT, open } = this.props
    const { testToPerformObj, isDOT, availableAlcoholTestSampleTypes } = this.state

    let sampleType = "";
    if(testToPerformObj){
      sampleType = testToPerformObj.Code;
    }
    if(sampleType === ""){
      if(availableAlcoholTestSampleTypes){
        if(availableAlcoholTestSampleTypes.length > 0){
          sampleType = availableAlcoholTestSampleTypes[0].Code;
        }
      }
    }
    if(sampleType === ""){
      if(this.props.orderableServices.length > 0){
        sampleType = this.props.orderableServices[0].SampleType || "";
      }
    }
    
    const orderableServices = ServicesByServiceSampleType(this.props.orderableServices, sampleType);
    const orderableAccountServices = this.props.orderableAccountServices
    const marketplaceServicesCount = this.props.marketplaceServicesCount;
    const notMarketplaceServicesCount = this.props.notMarketplaceServicesCount;

    const canAddMarketplace = notMarketplaceServicesCount === 0;
    const canAddNotMarketplace = marketplaceServicesCount === 0;

    let servicesComponents = [];
    this.isAllServicesDisabled = true;
    for (let i = 0; i < orderableServices.length; i++) {
      const orderableService = orderableServices[i];
      const serviceActive = IsServiceActive(orderableService, orderableAccountServices);
      const item_id = orderableService.DOT ? "regulated-alcohol-test-radio" : "non-regulated-alcohol-test-radio";
      //const item_value = orderableService.DOT ? "regulated" : "non-dot";
      const item_value = orderableService.FFServiceCode;

      const item_disabled = !serviceActive 
        || (orderableService.DOT  && (!canAddDOT || (!!this.props.selectedTestCode && isDOT !== true))) 
        || (!orderableService.DOT  && (!canAddNonDOT || (!!this.props.selectedTestCode && isDOT !== false)))
        || (orderableService.Marketplace && !canAddMarketplace)
        || (!orderableService.Marketplace && !canAddNotMarketplace)

        if(!item_disabled){
          this.isAllServicesDisabled = false;
        }        

      const item_checked = (isDOT === true && orderableService.DOT) || (isDOT === false && !orderableService.DOT)
      const serviceComponent = <Form.Check label={orderableService.ServiceDescription}
        id={item_id}
        key={item_id}
        type="radio"
        name="testType"
        className="font-weight-bold abc-radio"
        disabled={item_disabled}
        value={item_value}
        onChange={(e) => this.onChangeTestType(e.target.value)}
        checked={item_checked} />
      servicesComponents.push(serviceComponent)
    }
    return <>{servicesComponents}</>


  }

}
